import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { getConfig, loadRuntimeConfig } from '@/shared/config'
import i18n, { restoreLocale } from '@/shared/i18n'
import './styles/index.css'
import { App } from './App'

const container = document.getElementById('root')
if (!container) throw new Error('Не найден #root')

/**
 * Моки доступны в дев-сервере и в демо-сборке `pnpm build:mock` (режим `mock`).
 * Оба условия — литералы на этапе сборки: в боевой сборке ветка и динамический импорт
 * вырезаются целиком, и моковый API в прод попасть не может даже при `api: "mock"` в конфиге.
 */
const MOCKS_AVAILABLE = import.meta.env.DEV || import.meta.env.MODE === 'mock'

/**
 * Приложение не поднялось. Строка — из локали: русский словарь лежит в бандле и готов
 * до первого рендера, конфиг для этого не нужен.
 */
function showStartupError(root: HTMLElement, message: string, error: unknown): void {
  console.error('[bootstrap]', error)
  root.textContent = message
}

async function bootstrap(root: HTMLElement): Promise<void> {
  /*
   * Конфиг грузится до первого рендера: адреса API и режим моков нужны сразу. Вместе с ним —
   * словарь языка прошлого визита, иначе первый кадр мигнул бы русским. Сбой словаря запуск
   * не останавливает: restoreLocale его глотает и остаётся на русском.
   */
  try {
    await Promise.all([loadRuntimeConfig(), restoreLocale()])
  } catch (error) {
    showStartupError(root, i18n.t('app.bootstrap.configFailed'), error)
    return
  }

  if (MOCKS_AVAILABLE) {
    try {
      const { startMocks } = await import('@/mocks/browser')
      const { api, realDomains } = getConfig()
      await startMocks(api, realDomains)
    } catch (error) {
      // Конфиг на месте, не поднялся сервис-воркер моков: текст говорит именно об этом
      showStartupError(root, i18n.t('app.bootstrap.mocksFailed'), error)
      return
    }
  }

  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

void bootstrap(container)
